import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { 
  CreditCardIcon,
  BanknotesIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { useAuthStore } from '../../stores/authStore';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { useAnalytics, useComponentAnalytics } from '../../hooks/useAnalytics';

interface PaymentData {
  lawsuitId: string;
  lawsuitTitle: string;
  amount: number;
  currency: string;
  participantCount: number;
}

interface PaymentMethod {
  id: string;
  type: 'card' | 'bank' | 'paypal';
  name: string;
  icon: React.ComponentType<any>;
  description: string;
}

const PaymentPage: React.FC = () => {
  const { lawsuitId } = useParams<{ lawsuitId: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const analytics = useAnalytics();
  
  // Track component usage
  useComponentAnalytics('PaymentPage');

  const [paymentData, setPaymentData] = useState<PaymentData | null>(null);
  const [selectedMethod, setSelectedMethod] = useState<string>('card');
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [cardData, setCardData] = useState({
    number: '',
    expiry: '',
    cvv: '',
    name: '',
  });

  const paymentMethods: PaymentMethod[] = [
    {
      id: 'card',
      type: 'card',
      name: t('payment.creditCard', 'Credit Card'),
      icon: CreditCardIcon,
      description: t('payment.cardDescription', 'Pay securely with your credit or debit card')
    },
    {
      id: 'bank',
      type: 'bank', 
      name: t('payment.bankTransfer', 'Bank Transfer'),
      icon: BanknotesIcon,
      description: t('payment.bankDescription', 'Direct bank transfer (may take 1-3 business days)')
    }
  ];

  useEffect(() => {
    const fetchPaymentData = async () => {
      try {
        setIsLoading(true);
        // TODO: Replace with actual API call
        // const response = await axios.get(`/api/payments/lawsuit/${lawsuitId}`);
        // setPaymentData(response.data.data);
        
        // Mock data for now
        const mockPaymentData: PaymentData = {
          lawsuitId: lawsuitId || '1',
          lawsuitTitle: 'Class Action Against Tech Corp Data Breach',
          amount: 500,
          currency: 'USD',
          participantCount: 46
        };
        
        setPaymentData(mockPaymentData);
      } catch (err) {
        console.error('Failed to fetch payment data:', err);
        setError(t('errors.failedToLoad', 'Failed to load payment information'));
      } finally {
        setIsLoading(false);
      }
    };

    if (lawsuitId) {
      fetchPaymentData();
    }
  }, [lawsuitId, t]);

  const handleCardInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let formattedValue = value;

    if (name === 'number') {
      // Format card number with spaces
      formattedValue = value.replace(/\s/g, '').replace(/(.{4})/g, '$1 ').trim();
    } else if (name === 'expiry') {
      // Format expiry as MM/YY
      formattedValue = value.replace(/\D/g, '').replace(/(\d{2})(\d)/, '$1/$2');
    } else if (name === 'cvv') {
      // Limit CVV to 4 digits
      formattedValue = value.replace(/\D/g, '').slice(0, 4);
    }

    setCardData(prev => ({
      ...prev,
      [name]: formattedValue
    }));
  };

  const validateCardData = () => {
    const errors: string[] = [];
    
    if (!cardData.name.trim()) {
      errors.push(t('payment.nameRequired', 'Cardholder name is required'));
    }
    
    if (!cardData.number.replace(/\s/g, '') || cardData.number.replace(/\s/g, '').length < 13) {
      errors.push(t('payment.invalidCardNumber', 'Please enter a valid card number'));
    }
    
    if (!cardData.expiry || !/^\d{2}\/\d{2}$/.test(cardData.expiry)) {
      errors.push(t('payment.invalidExpiry', 'Please enter a valid expiry date'));
    }
    
    if (!cardData.cvv || cardData.cvv.length < 3) {
      errors.push(t('payment.invalidCvv', 'Please enter a valid CVV'));
    }

    return errors;
  };

  const handlePayment = async () => {
    if (!paymentData || !user) return;

    // Track payment initiation
    analytics.trackPayment('begin_checkout', {
      value: paymentData.amount / 100, // Convert cents to dollars
      currency: paymentData.currency,
      payment_type: selectedMethod,
      item_name: paymentData.lawsuitTitle,
      item_category: 'lawsuit_fee',
    });

    try {
      setIsProcessing(true);
      setError(null);

      if (selectedMethod === 'card') {
        const validationErrors = validateCardData();
        if (validationErrors.length > 0) {
          setError(validationErrors[0]);
          analytics.trackPayment('payment_failed', {
            value: paymentData.amount / 100,
            currency: paymentData.currency,
            payment_type: selectedMethod,
            item_name: paymentData.lawsuitTitle,
            item_category: 'lawsuit_fee',
          });
          return;
        }
      }

      // TODO: Replace with actual payment processing
      // const response = await axios.post('/api/payments/process', {
      //   lawsuitId: paymentData.lawsuitId,
      //   amount: paymentData.amount,
      //   currency: paymentData.currency,
      //   method: selectedMethod,
      //   cardData: selectedMethod === 'card' ? cardData : undefined
      // });

      // Simulate payment processing
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Track successful payment
      const transactionId = `txn_${Date.now()}_${paymentData.lawsuitId}`;
      analytics.trackPayment('purchase', {
        transaction_id: transactionId,
        value: paymentData.amount / 100,
        currency: paymentData.currency,
        payment_type: selectedMethod,
        item_name: paymentData.lawsuitTitle,
        item_category: 'lawsuit_fee',
      });

      // Navigate to success page
      navigate('/payment-success', {
        state: {
          lawsuitId: paymentData.lawsuitId,
          lawsuitTitle: paymentData.lawsuitTitle,
          amount: paymentData.amount,
          currency: paymentData.currency
        }
      });
    } catch (err) {
      console.error('Payment failed:', err);
      setError(t('payment.paymentFailed', 'Payment failed. Please try again.'));
      
      // Track payment failure
      analytics.trackPayment('payment_failed', {
        value: paymentData.amount / 100,
        currency: paymentData.currency,
        payment_type: selectedMethod,
        item_name: paymentData.lawsuitTitle,
        item_category: 'lawsuit_fee',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error && !paymentData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-500" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">{error}</h3>
          <div className="mt-6">
            <button
              onClick={() => navigate('/browse-lawsuits')}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-cyan-600 hover:bg-cyan-700"
            >
              {t('lawsuit.browseLawsuits', 'Browse Lawsuits')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{t('payment.payment', 'Payment')} - {paymentData?.lawsuitTitle} - tvia.ai</title>
        <meta name="description" content={`Complete payment for lawsuit participation: ${paymentData?.lawsuitTitle}`} />
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900">
              {t('payment.completePayment', 'Complete Payment')}
            </h1>
            <p className="mt-2 text-lg text-gray-600">
              {t('payment.securePayment', 'Your payment is secure and encrypted')}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Payment Form */}
            <div className="lg:col-span-2">
              {/* Payment Methods */}
              <div className="bg-white shadow rounded-lg mb-6">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">
                    {t('payment.paymentMethod', 'Payment Method')}
                  </h2>
                </div>
                <div className="p-6">
                  <div className="space-y-4">
                    {paymentMethods.map((method) => (
                      <div key={method.id} className="relative">
                        <input
                          type="radio"
                          id={method.id}
                          name="paymentMethod"
                          value={method.id}
                          checked={selectedMethod === method.id}
                          onChange={(e) => setSelectedMethod(e.target.value)}
                          className="sr-only"
                        />
                        <label
                          htmlFor={method.id}
                          className={`relative block cursor-pointer rounded-lg border p-4 focus:outline-none ${
                            selectedMethod === method.id
                              ? 'border-cyan-500 ring-2 ring-cyan-500'
                              : 'border-gray-300'
                          }`}
                        >
                          <div className="flex items-center">
                            <method.icon className="h-6 w-6 text-gray-400 mr-3" />
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900">
                                {method.name}
                              </p>
                              <p className="text-sm text-gray-500">
                                {method.description}
                              </p>
                            </div>
                            {selectedMethod === method.id && (
                              <CheckCircleIcon className="h-5 w-5 text-cyan-500" />
                            )}
                          </div>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Card Details */}
              {selectedMethod === 'card' && (
                <div className="bg-white shadow rounded-lg mb-6">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900">
                      {t('payment.cardDetails', 'Card Details')}
                    </h2>
                  </div>
                  <div className="p-6">
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="cardName" className="block text-sm font-medium text-gray-700">
                          {t('payment.cardholderName', 'Cardholder Name')}
                        </label>
                        <input
                          type="text"
                          id="cardName"
                          name="name"
                          value={cardData.name}
                          onChange={handleCardInputChange}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                          placeholder={t('payment.cardholderNamePlaceholder', 'John Doe')}
                        />
                      </div>
                      
                      <div>
                        <label htmlFor="cardNumber" className="block text-sm font-medium text-gray-700">
                          {t('payment.cardNumber', 'Card Number')}
                        </label>
                        <input
                          type="text"
                          id="cardNumber"
                          name="number"
                          value={cardData.number}
                          onChange={handleCardInputChange}
                          maxLength={19}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                          placeholder="1234 5678 9012 3456"
                        />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="cardExpiry" className="block text-sm font-medium text-gray-700">
                            {t('payment.expiryDate', 'Expiry Date')}
                          </label>
                          <input
                            type="text"
                            id="cardExpiry"
                            name="expiry"
                            value={cardData.expiry}
                            onChange={handleCardInputChange}
                            maxLength={5}
                            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                            placeholder="MM/YY"
                          />
                        </div>
                        
                        <div>
                          <label htmlFor="cardCvv" className="block text-sm font-medium text-gray-700">
                            CVV
                          </label>
                          <input
                            type="text"
                            id="cardCvv"
                            name="cvv"
                            value={cardData.cvv}
                            onChange={handleCardInputChange}
                            maxLength={4}
                            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                            placeholder="123"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Bank Transfer Instructions */}
              {selectedMethod === 'bank' && (
                <div className="bg-white shadow rounded-lg mb-6">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900">
                      {t('payment.bankTransferInstructions', 'Bank Transfer Instructions')}
                    </h2>
                  </div>
                  <div className="p-6">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-sm text-blue-800 mb-4">
                        {t('payment.bankInstructions', 'Please transfer the exact amount to the following account:')}
                      </p>
                      <div className="space-y-2 text-sm">
                        <div><strong>Account Name:</strong> <span className="font-brand">tvia.ai</span> Legal Services</div>
                        <div><strong>Account Number:</strong> 1234567890</div>
                        <div><strong>Routing Number:</strong> 987654321</div>
                        <div><strong>Reference:</strong> Lawsuit #{paymentData?.lawsuitId}</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Error Display */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                  <div className="flex">
                    <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-3" />
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white shadow rounded-lg p-6 sticky top-8">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  {t('payment.orderSummary', 'Order Summary')}
                </h2>
                
                <div className="space-y-4">
                  <div>
                    <h3 className="font-medium text-gray-900">
                      {paymentData?.lawsuitTitle}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {t('payment.participationFee', 'Participation Fee')}
                    </p>
                  </div>
                  
                  <div className="border-t border-gray-200 pt-4">
                    <div className="flex justify-between">
                      <span className="text-base font-medium text-gray-900">
                        {t('payment.total', 'Total')}
                      </span>
                      <span className="text-base font-medium text-gray-900">
                        ${paymentData?.amount} {paymentData?.currency}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mt-6">
                  <button
                    onClick={handlePayment}
                    disabled={isProcessing}
                    className="w-full inline-flex items-center justify-center px-4 py-3 border border-transparent text-base font-medium rounded-md text-white bg-cyan-600 hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isProcessing ? (
                      <LoadingSpinner size="sm" />
                    ) : (
                      <>
                        <ShieldCheckIcon className="h-5 w-5 mr-2" />
                        {t('payment.completePayment', 'Complete Payment')}
                      </>
                    )}
                  </button>
                </div>

                <div className="mt-4 text-xs text-gray-500 text-center">
                  <p>
                    {t('payment.secureMessage', 'Your payment information is encrypted and secure.')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PaymentPage; 